// src/Medicalcase/components/GoLiveStreamForm/GoLiveStreamForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getSpecialtyLabel } from "../Specialtyfilter/specialtyLabel";
// ↑ Adaptez ce chemin si GoLiveStreamForm et Specialtyfilter ne partagent
// pas le même dossier parent (components/).
import "./GoLiveStreamForm.css";

const IVideo = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 7l-8.5 5L22 17V7z" />
    <rect x="2" y="5" width="13" height="14" rx="2" ry="2" />
  </svg>
);

const IChevron = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Liste par défaut des spécialités proposées pour un live.
// IMPORTANT : ce sont les VALEURS ANGLAISES (mêmes clés que dans
// SPECIALTIES / specialties.json et envoyées telles quelles à l'API).
// Le libellé affiché est calculé via getSpecialtyLabel(t, value), donc
// aucune valeur ici n'est traduite en dur.
const DEFAULT_SPECIALTIES = [
  "Cardiology",
  "Neurology",
  "Medical Oncology",
  "Orthopedics",
  "Radiology",
  "Pediatrics",
  "Dermatology",
  "Psychiatry",
  "Gastroenterology",
  "Endocrinology",
  "Pulmonology",
  "Nephrology",
  "Rheumatology",
  "Urology",
  "Ophthalmology",
  "Otorhinolaryngology (ENT)",
  "Gynecology",
  "General Surgery",
  "Anesthesiology",
  "Emergency Medicine",
  "Critical Care / Intensive Care",
  "Intensive Medicine",
  "Anesthesiology and Intensive Care",
  "General",
];

export default function GoLiveStreamForm({ specialties = [], onBack, onCancel, onSubmit }) {
  const { t, i18n } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [maxParticipants, setMaxParticipants] = useState(20);
  const [isSpecialtyOpen, setIsSpecialtyOpen] = useState(false);

  const comboRef = useRef(null);

  // Direction du texte selon la langue active (ar => rtl),
  // même logique que dans Specialtyfilter.jsx.
  const isRTL = i18n.dir(i18n.language) === "rtl";

  // Construit la liste finale des spécialités : on garde la VALEUR brute
  // (utilisée pour le formulaire / l'API) et on calcule le LIBELLÉ traduit
  // via getSpecialtyLabel, exactement comme dans SpecialtyFilter.jsx.
  // Si le parent fournit des `specialties` (valeurs brutes venant de
  // MedicalCaseDiscussions), on les utilise ; sinon on retombe sur la
  // liste par défaut ci-dessus.
  const specialtyOptions = useMemo(() => {
    const provided = (specialties || []).filter(Boolean);
    const source = provided.length ? provided : DEFAULT_SPECIALTIES;
    return source.map((value) => ({
      value,
      label: getSpecialtyLabel(t, value),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialties, t, i18n.language]);

  const selectedSpecialtyLabel = useMemo(() => {
    const found = specialtyOptions.find((opt) => opt.value === specialty);
    return found ? found.label : "";
  }, [specialtyOptions, specialty]);

  // Ferme la liste déroulante au clic en dehors ou à la touche Échap
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboRef.current && !comboRef.current.contains(event.target)) {
        setIsSpecialtyOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsSpecialtyOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleSelectSpecialty = (value) => {
    setSpecialty(value);
    setIsSpecialtyOpen(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!title.trim()) return;

    const parsedMaxParticipants = Number(maxParticipants);
    const normalizedMaxParticipants = Number.isFinite(parsedMaxParticipants)
      ? Math.max(2, Math.round(parsedMaxParticipants))
      : 20;

    const payload = {
      title: title.trim(),
      description: description.trim(),
      specialty,
      privacy,
      maxParticipants: normalizedMaxParticipants,
    };

    if (onSubmit) onSubmit(payload);
  };

  return (
    <section
      className="mcd-live-form"
      aria-label={t("caseDiscussions.liveFormTitle", { defaultValue: "Go Live with a Case Presentation" })}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <form className="mcd-live-form__card" onSubmit={handleSubmit}>
        <button type="button" className="mcd-live-form__back" onClick={onBack}>
          <span aria-hidden="true">{isRTL ? "→" : "←"}</span>
          {t("caseDiscussions.backToCases", { defaultValue: "Back to Cases" })}
        </button>

        <div className="mcd-live-form__icon-wrap" aria-hidden="true">
          <span className="mcd-live-form__icon">
            <IVideo />
          </span>
        </div>

        <header className="mcd-live-form__header">
          <h2>{t("caseDiscussions.liveFormTitle", { defaultValue: "Go Live with a Case Presentation" })}</h2>
          <p>{t("caseDiscussions.liveFormSubtitle", { defaultValue: "Stream a live case discussion and interact with colleagues in real-time" })}</p>
        </header>

        <div className="mcd-live-form__field">
          <label htmlFor="stream-title">
            {t("caseDiscussions.liveFormStreamTitle", { defaultValue: "Stream Title" })} <span>*</span>
          </label>
          <input
            id="stream-title"
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t("caseDiscussions.liveFormStreamPlaceholder", { defaultValue: "e.g., Complex Arrhythmia Case Discussion" })}
            required
          />
        </div>

        <div className="mcd-live-form__field">
          <label htmlFor="stream-description">{t("caseDiscussions.liveFormDescription", { defaultValue: "Description" })}</label>
          <textarea
            id="stream-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t("caseDiscussions.liveFormDescriptionPlaceholder", { defaultValue: "Brief overview of what you'll be presenting..." })}
            rows={4}
          />
        </div>

        <div className="mcd-live-form__row">
          <div className="mcd-live-form__field">
            <label htmlFor="stream-specialty">
              {t("caseDiscussions.liveFormSpecialty", { defaultValue: "Specialty" })}
            </label>

            {/* Liste déroulante personnalisée : hauteur réduite + scroll interne */}
            <div className="mcd-live-form__combo" ref={comboRef}>
              <button
                type="button"
                id="stream-specialty"
                className="mcd-live-form__combo-trigger"
                onClick={() => setIsSpecialtyOpen((open) => !open)}
                aria-haspopup="listbox"
                aria-expanded={isSpecialtyOpen}
              >
                <span className={selectedSpecialtyLabel ? "" : "mcd-live-form__combo-placeholder"}>
                  {selectedSpecialtyLabel || t("caseDiscussions.liveFormSelectSpecialty", { defaultValue: "Select specialty" })}
                </span>
                <span className={`mcd-live-form__combo-chevron${isSpecialtyOpen ? " is-open" : ""}`}>
                  <IChevron />
                </span>
              </button>

              {isSpecialtyOpen && (
                <ul
                  className="mcd-live-form__combo-list"
                  role="listbox"
                  aria-label={t("caseDiscussions.liveFormSpecialty", { defaultValue: "Specialty" })}
                >
                  <li
                    role="option"
                    aria-selected={specialty === ""}
                    className={`mcd-live-form__combo-option${specialty === "" ? " is-selected" : ""}`}
                    onClick={() => handleSelectSpecialty("")}
                  >
                    {t("caseDiscussions.liveFormSelectSpecialty", { defaultValue: "Select specialty" })}
                  </li>
                  {specialtyOptions.map((item) => (
                    <li
                      key={item.value}
                      role="option"
                      aria-selected={specialty === item.value}
                      className={`mcd-live-form__combo-option${specialty === item.value ? " is-selected" : ""}`}
                      onClick={() => handleSelectSpecialty(item.value)}
                    >
                      {item.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="mcd-live-form__field">
            <label htmlFor="stream-max-participants">
              {t("caseDiscussions.liveFormMaxParticipants", { defaultValue: "Max Participants" })}
            </label>
            <input
              id="stream-max-participants"
              type="number"
              min="2"
              step="1"
              value={maxParticipants}
              onChange={(event) => setMaxParticipants(Number(event.target.value))}
              required
            />
          </div>
        </div>

        <div className="mcd-live-form__row">
          <div className="mcd-live-form__field">
            <label htmlFor="stream-privacy">{t("caseDiscussions.liveFormPrivacy", { defaultValue: "Privacy" })}</label>
            <select id="stream-privacy" value={privacy} onChange={(event) => setPrivacy(event.target.value)}>
              <option value="public">{t("caseDiscussions.liveFormPrivacyPublic", { defaultValue: "Public to verified doctors" })}</option>
              <option value="private">{t("caseDiscussions.liveFormPrivacyPrivate", { defaultValue: "Private invite only" })}</option>
            </select>
          </div>
        </div>

        <aside className="mcd-live-form__guidelines" aria-label={t("caseDiscussions.liveFormGuidelinesTitle", { defaultValue: "Live Stream Guidelines" })}>
          <h3>{t("caseDiscussions.liveFormGuidelinesTitle", { defaultValue: "Live Stream Guidelines" })}</h3>
          <ul>
            <li>{t("caseDiscussions.liveFormGuideline1", { defaultValue: "Ensure all patient information is completely de-identified" })}</li>
            <li>{t("caseDiscussions.liveFormGuideline2", { defaultValue: "Use professional medical terminology" })}</li>
            <li>{t("caseDiscussions.liveFormGuideline3", { defaultValue: "Engage with viewer questions in real-time" })}</li>
            <li>{t("caseDiscussions.liveFormGuideline4", { defaultValue: "Follow HIPAA compliance guidelines" })}</li>
          </ul>
        </aside>

        <div className="mcd-live-form__actions">
          <button type="submit" className="mcd-live-form__submit" disabled={!title.trim()}>
            <IVideo />
            {t("caseDiscussions.startLiveStream", { defaultValue: "Start Live Stream" })}
          </button>

          <button type="button" className="mcd-live-form__cancel" onClick={onCancel}>
            {t("feedbackPopup.cancel", { defaultValue: "Cancel" })}
          </button>
        </div>
      </form>
    </section>
  );
}